import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { CloseKit, Edit, PlayStroke } from 'education-icons';
import {
    Button,
    Input,
    InputChangeEvent,
    SymbolsCounter,
    Text,
    useGeneratePath,
    useIntl,
    useStaticUrls,
} from 'platform-components';
import { INPUT_SIZES, INPUT_THEMES, UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components/constants';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { actions } from '../actions';
import {
    getSendStatus,
    getQuizIsValidStatus,
    getQuizDraft,
    getViewType,
    isReadOnly,
    getTitleMaxLength,
} from '../selectors';
import { useQuizDraftInLocalStorage } from '../useQuizDraftInLocalStorage';
import { getEmptyQuizTemplate } from '../utils';

import './quiz-editor__header.scss';

interface ParamType {
    uuid?: string;
}

// eslint-disable-next-line complexity
export const QuizEditorHeader = ({
    onShowPreviewAnswersToggle,
    showPreviewAnswers = false,
}: {
    onShowPreviewAnswersToggle?: (showPreviewAnswers: boolean) => void;
    showPreviewAnswers?: boolean;
}) => {
    const intl = useIntl();
    const quizTitlePlaceholder = intl.formatMessage({ id: 'teacherLab.quizEditor.quizTitlePlaceholder' });

    const history = useHistory();
    const { labEditQuiz, labQuiz } = useStaticUrls();
    const generatePath = useGeneratePath();
    const dispatch = useDispatch();
    const { uuid } = useParams<ParamType>();

    const isQuizValid = useSelector(getQuizIsValidStatus);
    const sendStatus = useSelector(getSendStatus);
    const quiz = useSelector(getQuizDraft);
    const readOnly = useSelector(isReadOnly);
    const view = useSelector(getViewType);
    const titleMaxLength = useSelector(getTitleMaxLength);

    const shouldShowEditButton = view === 'edit' && readOnly;

    const { saveQuizDraftInLocalStorage, deleteQuizDraftFromLocalStorage } = useQuizDraftInLocalStorage();

    const [quizTitle, setQuizTitle] = useState(quiz.title);
    const [isDisabled, setIsDisabled] = useState(!isQuizValid);
    const [isEditing, setIsEditing] = useState(false);

    const deferredSavingTimer = useRef(0);

    useEffect(() => {
        deferredSavingTimer.current = window.setTimeout(() => {
            dispatch(actions.setQuizTitle(quizTitle));
        }, 400);

        return () => {
            window.clearTimeout(deferredSavingTimer.current);
        };
    }, [dispatch, quizTitle]);

    useEffect(() => {
        if (view === 'create') {
            switch (sendStatus) {
                case LOADING_STATUSES.ERROR:
                    saveQuizDraftInLocalStorage(quiz);
                    break;
                case LOADING_STATUSES.SUCCESS:
                    deleteQuizDraftFromLocalStorage();
                    break;
            }
        }
    }, [deleteQuizDraftFromLocalStorage, quiz, saveQuizDraftInLocalStorage, sendStatus, view]);

    useEffect(() => {
        setQuizTitle(quiz.title);
    }, [quiz]);

    useEffect(() => {
        setIsDisabled(!isQuizValid);
    }, [isQuizValid]);

    const saveQuizDraft = useCallback(() => {
        if (sendStatus === LOADING_STATUSES.UNSENT && view === 'create') {
            saveQuizDraftInLocalStorage(quiz);
        }
    }, [quiz, saveQuizDraftInLocalStorage, sendStatus, view]);

    useEffect(() => {
        window.addEventListener('blur', saveQuizDraft);
        window.addEventListener('pagehide', saveQuizDraft);
        window.addEventListener('beforeunload', saveQuizDraft);
        document.addEventListener('visibilitychange', saveQuizDraft);

        return function () {
            window.removeEventListener('blur', saveQuizDraft);
            window.removeEventListener('pagehide', saveQuizDraft);
            window.removeEventListener('beforeunload', saveQuizDraft);
            document.removeEventListener('visibilitychange', saveQuizDraft);
        };
    });

    const handleSaveClick = () => {
        if (view === 'create') {
            dispatch(actions.createQuiz());
            return;
        }

        dispatch(actions.updateQuiz());
    };

    const handleEditClick = () => {
        history.push(
            generatePath(labEditQuiz, {
                uuid: quiz.id,
            })
        );
    };

    const handleInputBlur = () => {
        dispatch(actions.setQuizTitle(quizTitle));
        setIsEditing(false);
    };

    const handleInputChange = (event: InputChangeEvent) => {
        setQuizTitle(event.target.value);
    };

    const handleCloseClick = () => {
        if (view === 'create' && sendStatus === LOADING_STATUSES.UNSENT) {
            saveQuizDraftInLocalStorage(quiz);
        }
        history.push(labQuiz);
    };

    const handleStartClick = () => {
        if (view === 'create') {
            saveQuizDraftInLocalStorage(quiz);
        }
        dispatch(actions.setIsQuizDraftStarted(true));
        history.push(labQuiz);
    };

    const handleTitleClick = () => {
        if (!readOnly) {
            setIsEditing(true);
        }
    };

    const handleClearClick = () => {
        const emptyQuiz = getEmptyQuizTemplate(uuid);
        dispatch(actions.setQuizDraft(emptyQuiz));
    };

    return (
        <div className="quiz-editor__header">
            <div className="quiz-editor__title-wrapper">
                {isEditing ? (
                    <>
                        <Input
                            focused
                            maxLength={titleMaxLength}
                            mix="quiz-editor__title-input"
                            onBlur={handleInputBlur}
                            onChange={handleInputChange}
                            placeholder={quizTitlePlaceholder}
                            size={INPUT_SIZES.XL}
                            theme={INPUT_THEMES.BORDERLESS}
                            value={quizTitle}
                        />
                        <SymbolsCounter
                            className="quiz-editor__title-symbols-counter"
                            current={quizTitle.length}
                            limit={titleMaxLength}
                        />
                    </>
                ) : (
                    <div className="quiz-editor__title" {...accessibleClick(handleTitleClick)}>
                        <Text className="quiz-editor__title-text" typography={UI_KIT_TEXT_TYPOGRAPHY.headlineXSMedium}>
                            {quizTitle || quizTitlePlaceholder}
                        </Text>
                        {!readOnly && <Edit className="quiz-editor__title-edit-icon" />}
                    </div>
                )}
            </div>
            <div className="quiz-editor__controls">
                {readOnly && onShowPreviewAnswersToggle && (
                    <Button
                        onClick={() => {
                            onShowPreviewAnswersToggle(!showPreviewAnswers);
                        }}
                        view={Button.VIEW.CLEAR}
                    >
                        <Text
                            id={
                                showPreviewAnswers
                                    ? 'teacherLab.quizEditor.showPreviewAnswersNo'
                                    : 'teacherLab.quizEditor.showPreviewAnswersYes'
                            }
                        />
                    </Button>
                )}
                {shouldShowEditButton && (
                    <Button icon={<Edit />} onClick={handleEditClick} view={Button.VIEW.SECONDARY}>
                        <Text id="teacherLab.quizEditor.editButton" />
                    </Button>
                )}
                {!readOnly && (
                    <>
                        <Button onClick={handleClearClick} view={Button.VIEW.SECONDARY}>
                            <Text id="teacherLab.quizEditor.clearButton" />
                        </Button>
                        <Button
                            disabled={isDisabled || sendStatus === LOADING_STATUSES.SUCCESS}
                            onClick={handleSaveClick}
                            progress={sendStatus === LOADING_STATUSES.LOADING}
                            view={Button.VIEW.SECONDARY}
                        >
                            <Text id="teacherLab.quizEditor.saveButton" />
                        </Button>
                    </>
                )}
                <Button
                    disabled={isDisabled}
                    icon={<PlayStroke />}
                    onClick={handleStartClick}
                    view={Button.VIEW.ACTION}
                >
                    <Text id="teacherLab.quizEditor.startQuizButton" />
                </Button>
                <div className="quiz-editor__close" {...accessibleClick(handleCloseClick)}>
                    <CloseKit className="quiz-editor__close-icon" />
                </div>
            </div>
        </div>
    );
};
